import { useState, lazy, Suspense, useCallback, } from "react";
import { useIndicadores } from "../services/indicatorService";
import DatagridTable from "../components/dashboard/common/DatagridTable";
import { Box, Button, Dialog, IconButton, Link as MuiLink, Typography } from "@mui/material";
import { showAlert } from "../utils/alert";
import { toggleIndicadorStatus } from "../services/indicatorService";
import { Link } from "react-router-dom";
import { intlFormat, parseISO } from 'date-fns'
import PageHeader from "../components/dashboard/common/DataHeader";
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import useIsMounted from "../hooks/useIsMounted";
import SearchInput from "../components/dashboard/common/SearchInput";
import useQueryParams, { useSearch } from "../hooks/useQueryParams";
import { Status } from "../components/dashboard/common/Status";
import Loader from "react-spinners/BarLoader";


export const Indicators = () => {
  const { updateFilters, updatePage, updatePerPage, params } = useQueryParams()
  const { page, perPage, filters } = params;
  const { updateSearchQuery, searchQuery } = useSearch();
  const { indicadores, isLoading, mutate, total } = useIndicadores({ page, perPage, searchQuery: searchQuery, ...filters });
  const [selectedIndicadores, setSelectedIndicadores] = useState([]);

  const columns = [
    {
      field: "id",
      headerName: "ID ",
      flex: 1,
      maxWidth: 50,
      editable: false,
      headerAlign: 'right',
      align: 'right',
    },
    {
      field: "nombre",
      headerName: "Nombre",
      flex: 2,
      minWidth: 200,
      editable: false,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => (
        <MuiLink component={Link} to={`/indicadores/${params.row.id}`}>{params.row.nombre}</MuiLink>
      )
    },
    {
      field: "ultimoValorDisponible",
      headerName: "Valor actual",
      flex: 1,
      minWidth: 100,
      headerAlign: 'right',
      align: 'right',
      valueGetter: (params) => new Intl.NumberFormat('es-MX', { style: 'decimal' }).format(params.row.ultimoValorDisponible)
    },
    {
      field: "adornment",
      headerName: "Simbolo",
      flex: 1,
      maxWidth: 80,
      editable: false,
      sortable: false,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: "unidadMedida",
      headerName: "Unidad de medida",
      flex: 1,
      minWidth: 150,
      editable: false,
      sortable: false,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: "responsable",
      headerName: "Responsable",
      flex: 1,
      minWidth: 80,
      editable: false,
      sortable: false,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (params) => params.row.responsable.length > 0 ? params.row.responsable[0].nombres : ''
    },
    {
      field: "periodicidad",
      headerName: "Periodicidad",
      flex: 1,
      maxWidth: 120,
      editable: false,
      sortable: false,
      headerAlign: 'left',
      align: 'left',
      valueGetter: params => `Cada ${params.row.periodicidad === 1 ? 'mes' : `${params.row.periodicidad} meses`}`,
    },
    {
      field: "updatedAt",
      headerName: "Última actualización",
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: params => intlFormat(parseISO(params.row.updatedAt), { locale: 'es-MX' })
    },
    {
      field: "createdAt",
      headerName: "Creado",
      headerAlign: 'left',
      align: 'left',
      hide: true,
      valueGetter: params => intlFormat(parseISO(params.row.createdAt), { locale: 'es-MX' })
    },
    {
      field: "activo",
      headerName: "Estado",
      editable: false,
      flex: 1,
      renderCell: (params) => (
        <Status handleClick={() => toggleStatus(params.row, mutate)} status={params.row.activo} />
      ),
    },
  ];

  const rowSelectionHandler = (ids) => {
    const selectedData = ids.map(id => indicadores.find(row => row.id === id));
    setSelectedIndicadores(selectedData);
  }
  const { owner, objetivos = [], temas = [], usuarios = [] } = filters;
  const hasActiveFilters = !!owner || [objetivos, temas, usuarios].some(arr => arr?.length > 0);
  return (
    <Box display='flex' flexDirection='column' p={2} height='100%'>
      <PageHeader
        title='Indicadores'
        SearchBar={
          <SearchInput
            placeholder='Buscar por nombre, unidad de medida o código'
            onDebouncedChange={updateSearchQuery}
            AdvancedSearch={<IndicadoresFilterDialog
              submitCallback={updateFilters}
              hasActiveFilters={hasActiveFilters}
            />}
          />
        }
      >
        <Button
          variant="outlined"
          onClick={() => mutate()}
          color="primary"
        >
          <RefreshIcon />
        </Button>
        <NewIndicadorDialog />
        <NewRelationDialog selectedIndicadores={selectedIndicadores} setSelectedIndicadores={setSelectedIndicadores} />
      </PageHeader>

      <div className='datagrid-container'>
        <DatagridTable
          rows={indicadores}
          columns={columns}
          isLoading={isLoading}
          page={page}
          total={total}
          perPage={perPage}
          onSelectionModelChange={rowSelectionHandler}
          handlePageChange={updatePage}
          handlePageSizeChange={updatePerPage}
          selectionModel={selectedIndicadores.map(item => item.id)}
        />
      </div>
    </Box>
  );
};


const toggleStatus = (indicador, successCallback) => {
  showAlert({
    title: `¿Deseas cambiar el estado de '${indicador.nombre}'?`,
    text: `Al actualizar este registro, el estado del indicador se alternará, 
    es decir, si se encuentra ACTIVO pasará a estar INACTIVO y viceversa.`,
    icon: 'warning',
    showCancelButton: true
  }).then(option => {
    if (option.isConfirmed) {
      return toggleIndicadorStatus(indicador.id);
    }
  })
    .then(res => {
      if (res) {
        showAlert({
          title: 'Estado actualizado exitosamente',
          text: `El estado de '${indicador.nombre}' ha sido actualizado.`,
          icon: 'success'
        })
      }
    })
    .catch(err => {
      showAlert({
        title: 'Hubo un error',
        text: err,
        icon: 'error'
      })
    })
    .finally(() => {
      if (typeof successCallback === 'function') {
        return successCallback();
      }
    })
}


const FormFiltro = lazy(() => import("../components/dashboard/forms/indicador/FormFiltro"))


const IndicadoresFilterDialog = (props) => {
  const [open, setOpen] = useState(false);
  const isMounted = useIsMounted();

  const handleClose = useCallback(() => {
    if (isMounted()) {
      setOpen(false)
    }
  }, [isMounted])

  return (<>
    <IconButton onClick={() => setOpen(true)}>
      <FilterAltIcon color={props.hasActiveFilters ? 'primary' : 'action'} />
    </IconButton>
    <Dialog
      open={open}
      fullWidth
      maxWidth='sm'
      onClose={handleClose}
      disableScrollLock
      keepMounted
    >
      <Suspense fallback={<Typography mx={4} my={2}>Cargando...</Typography>}>
        <FormFiltro
          handleClose={handleClose}
          submitCallback={props.submitCallback}
        />
      </Suspense>
    </Dialog>
  </>)
}


const FormIndicador = lazy(() => import("../components/dashboard/forms/indicador/FormIndicador").then(module => ({ default: module.FormIndicador })));


const NewIndicadorDialog = () => {
  const [open, setOpen] = useState(false);
  const handleClose = useCallback(() => setOpen(false), [])

  return (<>
    <Button startIcon={<AddIcon />} onClick={() => setOpen(true)} variant='outlined'>Agregar Indicador</Button>
    <Dialog open={open} fullWidth maxWidth='md' onClose={handleClose}>
      <Suspense fallback={<Typography mx={4} my={2}>Cargando...</Typography>}>
        <FormIndicador close={handleClose} />
      </Suspense>
    </Dialog>
  </>);
}

const FormAssignation = lazy(() => import("../components/dashboard/forms/relationship/FormAssignation").then(module => ({ default: module.FormAssignation })));

const NewRelationDialog = ({ selectedIndicadores, setSelectedIndicadores }) => {
  const [open, setOpen] = useState(false);
  const handleClose = useCallback(() => setOpen(false), [])

  return (
    <>
      <Button disabled={selectedIndicadores.length === 0} startIcon={<PersonOutlineOutlinedIcon />} variant='outlined' onClick={() => setOpen(true)}>Asignar</Button>
      <Dialog open={open} fullWidth maxWidth='md' onClose={handleClose}>
        <Suspense fallback={<Loader
          color='#3f51b5'
          loading={true}
          height={4}
          width={100}
        />
        }>
          <FormAssignation indicadores={selectedIndicadores} setSelectedIndicadores={setSelectedIndicadores} close={handleClose} />
        </Suspense>
      </Dialog>
    </>
  )
}